"use client";

import { useEffect } from "react";

// global-error.jsx replaces the ROOT layout when it crashes.
// It MUST include <html> and <body> tags.

export default function GlobalError({ error, reset }) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Be Right Back</title>
      </head>

      <body
        style={{
          backgroundColor: "#F2F2F2",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
          margin: 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "32rem" }}>
          {/* Card */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "1rem",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            {/* Top accent bar */}
            <div
              style={{
                height: "6px",
                background:
                  "linear-gradient(to right, #9573F7, #b89dff, #283245)",
              }}
            />

            <div style={{ padding: "3rem 2.5rem", textAlign: "center" }}>
              {/* Icon — wrench/maintenance vibe */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    width: "5rem",
                    height: "5rem",
                    borderRadius: "9999px",
                    backgroundColor: "rgba(149,115,247,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="36"
                    height="36"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#9573F7"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l5.653-4.655m5.8-2.613.666-2.337a1.5 1.5 0 0 0-.994-1.86l-1.226-.41a1.5 1.5 0 0 1-.994-1.86l.41-1.226a1.5 1.5 0 0 0-1.86-.994l-2.337.666A4.5 4.5 0 0 0 6.75 9v.75"
                    />
                  </svg>
                </div>
              </div>

              {/* Badge */}
              <span
                style={{
                  display: "inline-block",
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.2em",
                  fontWeight: 600,
                  color: "#9573F7",
                  backgroundColor: "rgba(149,115,247,0.1)",
                  padding: "0.25rem 0.75rem",
                  borderRadius: "9999px",
                  marginBottom: "1rem",
                }}
              >
                We appreciate your patience.
              </span>

              {/* Heading */}
              <h1
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#283245",
                  marginBottom: "0.75rem",
                  lineHeight: 1.3,
                  marginTop: 0,
                }}
              >
                We're doing a quick refresh
              </h1>

              {/* Description */}
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  margin: "0 0 0.5rem",
                }}
              >
                We're briefly stepping away to keep things running smoothly. Sit
                tight - we'll be back up before you know it.
              </p>

              {error?.message &&
                process.env.NEXT_PUBLIC_ENV === "development" && (
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#9ca3af",
                      backgroundColor: "#f9fafb",
                      border: "1px solid #f3f4f6",
                      borderRadius: "0.5rem",
                      padding: "0.5rem 1rem",
                      marginTop: "1rem",
                      fontFamily: "monospace",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {error.message}
                  </p>
                )}
              {/* Divider */}
              <hr
                style={{
                  border: "none",
                  borderTop: "1px solid #f3f4f6",
                  margin: "2rem 0",
                }}
              />

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={reset}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#7a5bd1")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#9573F7")
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    backgroundColor: "#9573F7",
                    color: "#fff",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    padding: "0.625rem 1.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: "16rem",
                    transition: "background-color 0.2s",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                  Check again
                </button>

                <a
                  href="/"
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#9573F7";
                    e.currentTarget.style.color = "#9573F7";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                    e.currentTarget.style.color = "#283245";
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    color: "#283245",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    padding: "0.625rem 1.75rem",
                    borderRadius: "9999px",
                    textDecoration: "none",
                    width: "100%",
                    maxWidth: "16rem",
                    transition: "border-color 0.2s, color 0.2s",
                    boxSizing: "border-box",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                    />
                  </svg>
                  Go home
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p
            style={{
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginTop: "1.25rem",
            }}
          >
            Thank you for bearing with us - we'll be back shortly.
          </p>
        </div>
      </body>
    </html>
  );
}
